/// <amd-module name="Wiki/WitQuerySelector/FilteredQueriesTable" />
import * as React from "react";

import { ObservableValue } from "VSS/Core/Observable";
import { IListSelection, ListSelection } from "VSSUI/List";
import { ISimpleTableCell, ITableColumn, renderSimpleCell, Table } from "VSSUI/Table";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";

import { QueryHierarchyItem } from "Wiki/Clients/Wit/Contracts/WorkItemTracking";

import { getIconName, getQueryProperties, pathSeparator, QueryProperties, QueryTreeItem } from "Wiki/WitQuerySelector/Helper";
import * as Resources from "Wiki/WitQuerySelector/Resources";

export interface IFilteredQueriesTableProps {
    onQuerySelected: (queryId: string, queryProperties: QueryProperties, insertQuery?: boolean) => void;
    filteredQueries: QueryTreeItem[];
}

export class FilteredQueriesTable extends React.Component<IFilteredQueriesTableProps> {
    private static readonly s_nameColumnKey = "name";
    private static readonly s_pathColumnKey = "path";
    private _tableColumns: ITableColumn<ISimpleTableCell>[];
    private _selection: IListSelection;

    constructor(props: IFilteredQueriesTableProps) {
        super(props);
        this._tableColumns = this._getTableColumns();
        this._selection = new ListSelection(false);
    }

    public render(): JSX.Element {
        const { filteredQueries } = this.props;
        return filteredQueries.length === 0 ? (
            <div className="flex-row flex-grow justify-center flex-center secondary-text">{Resources.NoQueriesFound}</div>
        ) : (
            <Table
                columns={this._tableColumns}
                itemProvider={new ArrayItemProvider(_getSimpleTableCells(filteredQueries))}
                onSelect={this._onRowSelected}
                onActivate={this._onRowActivated}
                singleClickActivation={false}
                selection={this._selection}
                scrollable={true}
            />
        );
    }

    private _onSizeColumns = (event: MouseEvent, index: number, width: number): void => {
        (this._tableColumns[index].width as ObservableValue<number>).value = width;
    };

    private _getTableColumns(): ITableColumn<ISimpleTableCell>[] {
        return [
            {
                id: FilteredQueriesTable.s_nameColumnKey,
                name: Resources.NameColumnHeaderText,
                renderCell: renderSimpleCell,
                minWidth: 200,
                readonly: true,
                width: new ObservableValue(-100),
                onSize: this._onSizeColumns
            },
            {
                id: FilteredQueriesTable.s_pathColumnKey,
                name: Resources.PathColumnHeader,
                renderCell: renderSimpleCell,
                minWidth: 300,
                readonly: true,
                width: new ObservableValue(300),
                onSize: this._onSizeColumns
            }
        ];
    }

    private _onRowSelected = (event: React.SyntheticEvent<HTMLElement>, tableRow: { index: number }): void => {
        this._onFilteredQuerySelected(tableRow.index);
    };

    private _onRowActivated = (event: React.SyntheticEvent<HTMLElement>, tableRow: { index: number }): void => {
        this._onFilteredQuerySelected(tableRow.index, true);
    };

    private _onFilteredQuerySelected(index: number, insertQuery?: boolean): void {
        const selectedQuery = this.props.filteredQueries[index];
        this.props.onQuerySelected(selectedQuery.id, getQueryProperties(selectedQuery, true), insertQuery);
    }
}

// export for UTs
export function _getSimpleTableCells(queries: QueryHierarchyItem[]): ISimpleTableCell[] {
    return queries.map(query => {
        const path = query.path;
        const index = path.lastIndexOf(pathSeparator); // removing the file name to get just the folder path
        const iconName = getIconName(query.queryType);
        return {
            name: { iconProps: { iconName }, text: query.name },
            path: path.substring(0, index)
        };
    });
}
